/**
 * Модель для работы с картой
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';

import MapsService, { IUpdateMapBody } from '../services/MapsService';
import {
  IMap,
  ICoordXY,
  ICoordXYXY,
  AreaTypes,
  AreaModes,
  IId,
  EditPlanMode,
  ICoordXYZW
} from './types';

interface IControl {
  zoom: number;
  center: ICoordXY;
  bounds: ICoordXYXY;
}

interface ILayers {
  isGeo: boolean;
  isUsers: boolean;
  isAreas: boolean;
  onlyWorkingAreas: boolean;
  isInfrastructure: boolean;
  isHeatMap: boolean;
  isCameras: boolean;
}

interface IState {
  map: IMap;
  control: IControl;
  layers: ILayers;
  mode: AreaModes;
  type: AreaTypes;
  editPlan: boolean;
  editPlanMode: EditPlanMode;
  coordinates: any;
  planImg: string;
}

// const calcControl = ([q, w, e, r, t, y, u, i]: number[]) => ({
//   zoom: 19.5,
//   center: [q + u - 0.0001, w + i - 0.0001].map(coord => coord / 2),
//   bounds: []
// });

const initialState: IState = {
  control: {
    zoom: 13,
    center: [55.7558, 37.6173],
    bounds: [
      [0, 0],
      [0, 0]
    ]
  },
  map: {
    id: '',
    contentId: '',
    name: '',
    preview: '',
    previewFormat: '',
    bbox: [0, 0, 0, 0],
    gbox: [0, 0, 0, 0, 0, 0, 0, 0],
    areas: [],
    beacons: [],
    cameras: [],
    file: null
  },
  layers: {
    isGeo: true,
    isUsers: true,
    isAreas: true,
    onlyWorkingAreas: true,
    isInfrastructure: false,
    isHeatMap: false,
    isCameras: false
  },
  mode: AreaModes.Default,
  type: AreaTypes.Marker,
  editPlan: false,
  editPlanMode: EditPlanMode.Lock,
  coordinates: null,
  planImg: ''
};

export { initialState };

export const infrastructureMap = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setMap(state: IState, payload: IMap): IState {
      return { ...state, map: payload };
    },
    setFocus(state: IState, payload?: Partial<IControl>): IState {
      if (!payload) return state;
      return {
        ...state,
        control: {
          ...state.control,
          ...payload
        }
      };
    },
    setControl(state: IState, payload: IControl): IState {
      return {
        ...state,
        control: payload
      };
    },
    setLayers(state: IState, payload: Partial<ILayers>): IState {
      return {
        ...state,
        layers: {
          ...state.layers,
          ...payload
        }
      };
    },
    setEditPlan(state: IState, payload: boolean): IState {
      return {
        ...state,
        editPlan: payload
      };
    },
    setPlanImg(state: IState, payload: string): IState {
      return {
        ...state,
        planImg: payload
      };
    },
    resetPlanImg(state: IState): IState {
      return {
        ...state,
        planImg: ''
      };
    },
    setEditPlanMode(state: IState, payload: EditPlanMode): IState {
      return {
        ...state,
        editPlanMode: payload
      };
    },
    setCoordinates(state: IState, payload ): IState {
      return { ...state, coordinates: payload };
    },
    resetCoordinates(state: IState): IState {
      const { coordinates } = initialState;

      return { ...state, coordinates };
    },
    setDefaultMode(state: IState, _?: any): IState {
      return { ...state, mode: AreaModes.Default };
    },
    setBoxEditMode(state: IState, _?: any): IState {
      return { ...state, mode: AreaModes.Edit, type: AreaTypes.Box };
    },
    setCircleEditMode(state: IState, _?: any): IState {
      return { ...state, mode: AreaModes.Edit, type: AreaTypes.Circle };
    },
    setPolygonEditMode(state: IState, _?: any): IState {
      return { ...state, mode: AreaModes.Edit, type: AreaTypes.Polygon };
    },
    setMarkerEditMode(state: IState, _?: any): IState {
      return { ...state, mode: AreaModes.Edit, type: AreaTypes.Marker };
    },
    setBoxCreateMode(state: IState, _?: any): IState {
      return { ...state, mode: AreaModes.Create, type: AreaTypes.Box };
    },
    setCircleCreateMode(state: IState, _?: any): IState {
      return { ...state, mode: AreaModes.Create, type: AreaTypes.Circle };
    },
    setPolygonCreateMode(state: IState, _?: any): IState {
      return { ...state, mode: AreaModes.Create, type: AreaTypes.Polygon };
    },
    setMarkerCreateMode(state: IState, _?: any): IState {
      return { ...state, mode: AreaModes.Create, type: AreaTypes.Marker };
    }
  },
  effects: (dispatch: any) => ({

      resetFocus(payload?: any, rootState?: any) {
        // const { bbox, gbox } = rootState.infrastructureMap.map;
        // const { isGeo } = rootState.infrastructureMap.layers;
        // const focus = calcControl(gbox);
        // const { center } = isGeo ? calcGeoControl(gbox) : calcControl(bbox);
        // this.setFocus({ center });
      },
      async getMap(id?: any, rootState?: any) {
        const currentMapId = rootState.infrastructureMap.map.id;
        const defaultCurrentOfficeId = { id: null };
        const office = rootState.infrastructureOffices.item || defaultCurrentOfficeId;
        const { id: currentOfficeId } = office;
        const saveMapId = localStorage.getItem('mapId');

        let mapId = id || saveMapId;

        // Если выбрана опция "Все планы"
        if (mapId === 'all') {
          // Если место еще не загружено, загружаем его
          if (!currentOfficeId && !office.id) {
            // Если нет currentOfficeId, то берем сохраненный из localStorage
            const savedOfficeId = localStorage.getItem('officeId');
            if (savedOfficeId) {
              // Загружаем office по ID и устанавливаем
              try {
                // Используем infrastructureOffices для загрузки office по ID
                dispatch.infrastructureOffices.getOffice(savedOfficeId);
              } catch (error) {
                // Если ошибка, просто продолжаем с пустым office
              }
            }
          }
          
          // Переключаемся на гео подложку
          dispatch.infrastructureMap.setLayers({ isGeo: true });
          
          // Устанавливаем пустую карту (символизирующую "все планы")
          const emptyMap: IMap = {
            id: 'all',
            contentId: '',
            name: 'Все планы',
            preview: '',
            previewFormat: '',
            bbox: [0, 0, 0, 0],
            gbox: [0, 0, 0, 0, 0, 0, 0, 0],
            areas: [],
            beacons: [],
            cameras: [],
            file: null
          };
          
          this.setMap(emptyMap);
          // Обновляем URL в адресной строке
          window.history.pushState(null, '', '?mapId=all');
          // Загрузка пользователей будет выполнена в UserMarkerList.tsx
          return;
        }

        // Если нет ни одной карты, то выбираем по умолчанию
        if (!mapId && currentOfficeId) {
          const { data: maps } = await MapsService.getAll({ officeId: currentOfficeId });

          // MOCK
          const mapsReverse = maps.reverse();

          // Если планов больше одного, выбираем "Все планы", иначе выбираем первый план
          if (mapsReverse.length > 1) {
            mapId = 'all';
          } else {
            mapId = mapsReverse[0] && mapsReverse[0].id;
          }
        }

        // tslint:disable-next-line: triple-equals
        if (!mapId) {
          return;
        }

        // Повторная проверка для "Все планы" после выбора из списка
        if (mapId === 'all') {
          // Переключаемся на гео подложку
          dispatch.infrastructureMap.setLayers({ isGeo: true });
          
          // Устанавливаем фокус на координаты офиса
          if (office && office.location) {
            const officePosition: ICoordXY = [office.location.lat, office.location.lon];
            dispatch.infrastructureOffices.focus(officePosition);
          }
          
          // Устанавливаем пустую карту (символизирующую "все планы")
          const emptyMap: IMap = {
            id: 'all',
            contentId: '',
            name: 'Все планы',
            preview: '',
            previewFormat: '',
            bbox: [0, 0, 0, 0],
            gbox: [0, 0, 0, 0, 0, 0, 0, 0],
            areas: [],
            beacons: [],
            cameras: [],
            file: null
          };
          
          this.setMap(emptyMap);
          // Обновляем URL в адресной строке
          window.history.pushState(null, '', '?mapId=all');
          // Загрузка пользователей будет выполнена в UserMarkerList.tsx
          return;
        }

        const { data } = await MapsService.get(mapId);
        // Синхронизируем текущий офис с localStorage на основании выбранной карты
        if (data && data.place && data.place.id) {
          localStorage.setItem('officeId', String(data.place.id));
        }

        // Если меняем карту
        if (mapId !== currentMapId) {
          // то сбрасываем пользователей
          dispatch.infrastructureUsers.resetUsers();

          // то фиксируем карту
          localStorage.setItem('mapId', data.id);
        }

        // Если место отсутствует или не равно плану, то устанавливаем
        if (
          !rootState.infrastructureOffices.item ||
          rootState.infrastructureOffices.item.id !== data.place.id
        ) {
          dispatch.infrastructureOffices.setOffice(data.place);
        }

        // Перефокусируем
        const { bbox, gbox } = data;

        /* gbox может быть undefined, тогда не выполянем фокусировку на объекте,
        иначе появляются ошибки
        нужно уточнить, будет ли в дальнейшем поле gbox всегда корректным,
        тогда можно будет убрать эту проверку */
        if (gbox) {
          // const control = isGeo ? calcGeoControl(gbox) : calcControl(bbox);

          // this.setControl(control);
          window.history.pushState(null, '', '?mapId=' + mapId);
          this.setMap(data);
        }
      },
      async updateOrCreate(body: IUpdateMapBody) {
        const image = new FormData();
        if (body.file) {
          image.append('file', body.file);
        }
        try {
          if (body.file) {
            const { data } = await MapsService.updateOrCreateMapImage(image);
            await MapsService.updateOrCreateMap({ ...body, contentId: data });
          } else {
            await MapsService.updateOrCreateMap(body);
          }
        } catch (error) {
          return error;
        }
      },
      async getPlanImg(id: IId) {
        try {
          const data = await MapsService.getMapImage(id);
          this.setPlanImg(data);
        } catch (error) {
          return error;
        }
      },

      async remove(id: IId) {
        try {
          await MapsService.deleteMap(id);
        } catch (error) {
          return error;
        }
      },

      resetMap() {
        this.setMap(initialState.map);
      },

      async editMapPosition(file) {
        // console.log(file);
        // const file = e.target.files && e.target.files[0];
        if (file) {
          const reader = new FileReader();

          reader.onload = event => {
            if (event.target) {
              // console.log(file.size);
              // console.log(event.target.result as string);
            }
          };

          reader.readAsDataURL(file);
        }
      },

      showUsers(isUsers) {
        this.setLayers({ isUsers });
      },
      showAreas(isAreas) {
        this.setLayers({ isAreas });
      },
      showOnlyWorkingAreas(onlyWorkingAreas) {
        this.setLayers({ onlyWorkingAreas });
      },
      showInfrastructure(isInfrastructure) {
        this.setLayers({ isInfrastructure });
      },
      showHeatMap(isHeatMap) {
        this.setLayers({ isHeatMap });
      },
      showCameras(isCameras) {
        this.setLayers({ isCameras });
      }
    })
});
